'use client';

import { useState, useMemo } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import { generateActionPlan, ActionPlan, ActionItem, MonthPlan, WeekPlan } from '@/lib/actionPlanGenerator';
import styles from './ActionPlanDisplay.module.css';

interface ActionPlanDisplayProps {
  career: CareerPath;
  userSegment?: string | null;
  userStrengths?: string[];
}

export default function ActionPlanDisplay({
  career,
  userSegment = null,
  userStrengths = []
}: ActionPlanDisplayProps) {
  const [activeMonth, setActiveMonth] = useState(1);
  const [expandedWeeks, setExpandedWeeks] = useState<number[]>([1]);
  const [completedActions, setCompletedActions] = useState<string[]>([]);
  const [showQuickWins, setShowQuickWins] = useState(true);

  // Generate the action plan
  const plan: ActionPlan = useMemo(() => {
    return generateActionPlan(career, userSegment, userStrengths);
  }, [career, userSegment, userStrengths]);

  // Calculate progress
  const totalActions = useMemo(() => {
    let count = 0;
    for (const month of plan.months) {
      for (const week of month.weeks) {
        count += week.actions.length;
      }
    }
    return count;
  }, [plan]);

  const progressPercent = Math.round((completedActions.length / totalActions) * 100);

  const toggleWeek = (weekNum: number) => {
    setExpandedWeeks(prev =>
      prev.includes(weekNum)
        ? prev.filter(w => w !== weekNum)
        : [...prev, weekNum]
    );
  };

  const toggleAction = (actionId: string) => {
    setCompletedActions(prev =>
      prev.includes(actionId)
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  const getTypeIcon = (type: ActionItem['type']): string => {
    switch (type) {
      case 'learning': return '📚';
      case 'networking': return '🤝';
      case 'portfolio': return '💼';
      case 'application': return '📝';
      case 'certification': return '🎓';
      case 'research': return '🔍';
      case 'skill-building': return '⚡';
      default: return '📋';
    }
  };

  const getPriorityClass = (priority: ActionItem['priority']): string => {
    switch (priority) {
      case 'critical': return styles.priorityCritical;
      case 'high': return styles.priorityHigh;
      case 'medium': return styles.priorityMedium;
      default: return '';
    }
  };

  const getMonthProgress = (month: MonthPlan): number => {
    let total = 0;
    let completed = 0;
    for (const week of month.weeks) {
      for (const action of week.actions) {
        total++;
        if (completedActions.includes(action.id)) completed++;
      }
    }
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const currentMonth = plan.months.find(m => m.month === activeMonth);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>📅</div>
        <div className={styles.headerContent}>
          <h3>Your 90-Day Action Plan</h3>
          <p>{plan.overview.headline}</p>
        </div>
      </div>

      {/* Overview Cards */}
      <div className={styles.overviewGrid}>
        <div className={styles.overviewCard}>
          <span className={styles.overviewIcon}>⏱️</span>
          <span className={styles.overviewLabel}>Time to First Role</span>
          <span className={styles.overviewValue}>{plan.overview.timeToFirstRole}</span>
        </div>
        <div className={styles.overviewCard}>
          <span className={styles.overviewIcon}>📈</span>
          <span className={styles.overviewLabel}>Success Probability</span>
          <span className={`${styles.overviewValue} ${styles[`prob${plan.overview.successProbability}`]}`}>
            {plan.overview.successProbability === 'high' ? 'High' :
             plan.overview.successProbability === 'medium' ? 'Medium' : 'Achievable'}
          </span>
        </div>
        <div className={styles.overviewCard}>
          <span className={styles.overviewIcon}>💰</span>
          <span className={styles.overviewLabel}>Investment</span>
          <span className={styles.overviewValue}>{plan.overview.investmentRequired}</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className={styles.progressSection}>
        <div className={styles.progressHeader}>
          <span>Your Progress</span>
          <span className={styles.progressPercent}>{progressPercent}%</span>
        </div>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className={styles.progressStats}>
          <span>{completedActions.length} of {totalActions} actions completed</span>
        </div>
      </div>

      {/* Quick Wins */}
      {showQuickWins && plan.quickWins.length > 0 && (
        <div className={styles.quickWinsSection}>
          <div className={styles.quickWinsHeader}>
            <h4>⚡ Quick Wins - Start Today!</h4>
            <button
              className={styles.dismissButton}
              onClick={() => setShowQuickWins(false)}
            >
              ✕
            </button>
          </div>
          <div className={styles.quickWinsList}>
            {plan.quickWins.map((win) => (
              <div key={win.id} className={styles.quickWinItem}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={completedActions.includes(win.id)}
                    onChange={() => toggleAction(win.id)}
                    className={styles.checkbox}
                  />
                  <span className={completedActions.includes(win.id) ? styles.completed : ''}>
                    {win.title}
                  </span>
                </label>
                <span className={styles.quickWinTime}>{win.estimatedHours * 60}min</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Month Tabs */}
      <div className={styles.monthTabs}>
        {plan.months.map((month) => (
          <button
            key={month.month}
            className={`${styles.monthTab} ${activeMonth === month.month ? styles.activeMonthTab : ''}`}
            onClick={() => setActiveMonth(month.month)}
          >
            <span className={styles.monthNumber}>Month {month.month}</span>
            <span className={styles.monthTheme}>{month.theme}</span>
            <div className={styles.monthProgress}>
              <div
                className={styles.monthProgressFill}
                style={{ width: `${getMonthProgress(month)}%` }}
              />
            </div>
          </button>
        ))}
      </div>

      {/* Current Month Content */}
      {currentMonth && (
        <div className={styles.monthContent}>
          <div className={styles.monthHeader}>
            <h4>{currentMonth.theme}</h4>
            <p className={styles.monthGoal}>Goal: {currentMonth.goal}</p>
          </div>

          {/* Key Metrics */}
          <div className={styles.metricsGrid}>
            {currentMonth.keyMetrics.map((metric, idx) => (
              <div key={idx} className={styles.metricItem}>
                <span className={styles.metricCheck}>◯</span>
                <span>{metric}</span>
              </div>
            ))}
          </div>

          {/* Week Timeline */}
          <div className={styles.weekTimeline}>
            {currentMonth.weeks.map((week) => (
              <div key={week.week} className={styles.weekBlock}>
                <button
                  className={`${styles.weekHeader} ${expandedWeeks.includes(week.week) ? styles.expanded : ''}`}
                  onClick={() => toggleWeek(week.week)}
                >
                  <div className={styles.weekInfo}>
                    <span className={styles.weekNumber}>Week {week.week}</span>
                    <span className={styles.weekTheme}>{week.theme}</span>
                  </div>
                  <div className={styles.weekMeta}>
                    <span className={styles.weekMilestone}>{week.milestone}</span>
                    <span className={styles.expandIcon}>
                      {expandedWeeks.includes(week.week) ? '−' : '+'}
                    </span>
                  </div>
                </button>

                {expandedWeeks.includes(week.week) && (
                  <div className={styles.weekActions}>
                    <p className={styles.weekFocus}>Focus: {week.focus}</p>
                    <div className={styles.actionsList}>
                      {week.actions.map((action) => (
                        <div
                          key={action.id}
                          className={`${styles.actionCard} ${completedActions.includes(action.id) ? styles.actionCompleted : ''}`}
                        >
                          <div className={styles.actionHeader}>
                            <label className={styles.actionCheckbox}>
                              <input
                                type="checkbox"
                                checked={completedActions.includes(action.id)}
                                onChange={() => toggleAction(action.id)}
                              />
                              <span className={styles.customCheckbox}></span>
                            </label>
                            <span className={styles.actionIcon}>{getTypeIcon(action.type)}</span>
                            <h5 className={styles.actionTitle}>{action.title}</h5>
                            <span className={`${styles.actionPriority} ${getPriorityClass(action.priority)}`}>
                              {action.priority}
                            </span>
                          </div>
                          <p className={styles.actionDescription}>{action.description}</p>
                          <div className={styles.actionMeta}>
                            <span className={styles.actionTime}>⏱️ {action.estimatedHours}h</span>
                            <span className={styles.actionType}>{action.type.replace('-', ' ')}</span>
                          </div>

                          {/* Checkpoints */}
                          {action.checkpoints && action.checkpoints.length > 0 && (
                            <div className={styles.checkpoints}>
                              <span className={styles.checkpointsLabel}>Checkpoints:</span>
                              <ul>
                                {action.checkpoints.map((cp, idx) => (
                                  <li key={idx}>{cp}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Resources */}
                          {action.resources && action.resources.length > 0 && (
                            <div className={styles.resources}>
                              <span className={styles.resourcesLabel}>Resources:</span>
                              <div className={styles.resourceTags}>
                                {action.resources.map((resource, idx) => (
                                  <span key={idx} className={styles.resourceTag}>
                                    {resource.url ? (
                                      <a href={resource.url} target="_blank" rel="noopener noreferrer">
                                        {resource.name}
                                      </a>
                                    ) : (
                                      resource.name
                                    )}
                                    <span className={`${styles.resourceCost} ${styles[resource.cost]}`}>
                                      {resource.cost}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Critical Path */}
      <div className={styles.criticalPathSection}>
        <h4>🎯 Critical Path to Success</h4>
        <div className={styles.criticalPathList}>
          {plan.criticalPath.map((step, idx) => (
            <div key={idx} className={styles.criticalPathItem}>
              <span className={styles.criticalPathNumber}>{idx + 1}</span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Blockers & Indicators */}
      <div className={styles.insightsGrid}>
        <div className={styles.insightCard}>
          <h4>⚠️ Watch Out For</h4>
          <ul className={styles.blockersList}>
            {plan.potentialBlockers.map((blocker, idx) => (
              <li key={idx}>{blocker}</li>
            ))}
          </ul>
        </div>
        <div className={styles.insightCard}>
          <h4>✅ Success Indicators</h4>
          <ul className={styles.successList}>
            {plan.successIndicators.map((indicator, idx) => (
              <li key={idx}>{indicator}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Print/Export */}
      <div className={styles.exportSection}>
        <button
          className={styles.printButton}
          onClick={() => window.print()}
        >
          🖨️ Print Your Plan
        </button>
        <p className={styles.exportNote}>
          Your progress is saved in this browser. Bookmark this page to return to your plan.
        </p>
      </div>
    </div>
  );
}
